import { IPeerReviewManagementListView } from '@publishing-engine/domain/views/review';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState } from 'react';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';
import {
  fetchSubmissionReviews,
  ISubmissionSortOption
} from '../api/queries';
import { useSubmissionData } from './SubmissionDataProvider';
import { TABLE_PAGE_LIMIT } from 'src/constants';

interface ISubmissionReviewsDataContext {
  reviews: IPeerReviewManagementListView | null | undefined;
  hasSubmittedReviewInCurrentRound: (reviewId: string) => boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;

  searchParams: string;
  setSearchParams: (value: string) => void;
  status: string[];
  setStatus: (value: string[]) => void;
  sort: ISubmissionSortOption | null;
  setSort: (value: ISubmissionSortOption | null) => void;
  /**
   * Whether the round these reviews were fetched for is a revision round — the
   * same flag the request carries, and the one that says which of a review's
   * assessments belongs to the round on screen.
   */
  isRevisionRound: boolean;
  resetFilters: () => void;
}

export const SubmissionReviewsDataContext = createContext<
  ISubmissionReviewsDataContext | undefined
>(undefined);

export const useSubmissionReviewsData = (): ISubmissionReviewsDataContext => {
  const context = useContext(SubmissionReviewsDataContext);
  if (!context) {
    throw new Error(
      'useSubmissionReviewsData must be used within a SubmissionReviewsDataProvider'
    );
  }
  return context;
};

const SubmissionReviewsDataProvider = ({
  children
}: {
  children: JSX.Element;
}) => {
  const { submissionId, submissionContextId, isRevisionRound } =
    useSubmissionData();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);

  const [searchParams, setSearchParams] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [sort, setSort] = useState<ISubmissionSortOption | null>(null);

  const filteredStatus = status.filter(s => s !== '');

  const offset = (page - 1) * TABLE_PAGE_LIMIT;

  const handleSearchParamsChange = useCallback((value: string) => {
    setPage(1);
    setSearchParams(value);
  }, []);

  const handleStatusChange = useCallback((value: string[]) => {
    setPage(1);
    setStatus(value);
  }, []);

  const handleSortChange = useCallback(
    (value: ISubmissionSortOption | null) => {
      setPage(1);
      setSort(value);
    },
    []
  );

  const resetFilters = useCallback(() => {
    setSearchParams('');
    setStatus([]);
    setSort(null);
    setPage(1);

    // Invalidate the reviews query to force a refetch with reset filters
    queryClient.invalidateQueries({
      queryKey: [
        'submission-context',
        submissionContextId,
        submissionId,
        'reviews'
      ]
    });
  }, [queryClient, submissionContextId, submissionId]);

  const reviewsQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviews',
    {
      page,
      searchParams,
      ...(filteredStatus.length > 0 && { status: filteredStatus }),
      ...(sort && { sort })
    }
  ];

  const { data, isLoading, isError, error } = useQuery({
    queryKey: reviewsQueryKey,
    queryFn: () =>
      fetchSubmissionReviews(
        submissionContextId,
        submissionId,
        isRevisionRound,
        offset,
        TABLE_PAGE_LIMIT,
        searchParams,
        filteredStatus.length > 0 ? filteredStatus : undefined,
        sort ?? undefined
      ),
    keepPreviousData: true
  });

  if (isLoading) return <Loading />;
  if (isError) return isForbiddenError(error) ? <Forbidden /> : <NotFound />;

  const reviews = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / TABLE_PAGE_LIMIT);

  const hasSubmittedReviewInCurrentRound = (reviewId: string) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return false;

    return isRevisionRound
      ? !!review.caseData.revisionAssessment
      : !!review.caseData.firstAssessment;
  };

  return (
    <SubmissionReviewsDataContext.Provider
      value={{
        reviews: data,
        hasSubmittedReviewInCurrentRound,
        page,
        setPage,
        totalPages,
        searchParams,
        setSearchParams: handleSearchParamsChange,
        status,
        setStatus: handleStatusChange,
        sort,
        setSort: handleSortChange,
        isRevisionRound,
        resetFilters
      }}
    >
      {children}
    </SubmissionReviewsDataContext.Provider>
  );
};

export default SubmissionReviewsDataProvider;
